import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { CSSProperties, JSX, SelectHTMLAttributes } from 'react'
import { fieldCls, NumberInput } from './Form.tsx'
import { canHoverDevice } from './InfoTip.tsx'

/**
 * ══ Ô CHỌN — `Select` · `VatRateSelect` · `MultiSelect` · `SearchSelect` · `ComboInput` ══════════
 *
 * Chép `proship-trans/src/components/ui.tsx:589-957` (G2). Chú thích gốc giữ nguyên.
 * Dùng trong phạm vi RT: `Select` 70 · `SearchSelect` 79 · `MultiSelect` 60 · `ComboInput` 9 ·
 * `VatRateSelect` 7 lần.
 */

/** `ui.tsx:589`. */
export const Select = (
  { className = '', ...props }: SelectHTMLAttributes<HTMLSelectElement>,
): JSX.Element => <select {...props} className={`${fieldCls} ${className}`} />

/**
 * `ui.tsx:605`. Ô chọn THUẾ SUẤT VAT dùng chung (user 24/08: "cần thêm KCT và KHAC để lựa chọn,
 * check hệ thống cái nào bị fix hết") — trước mỗi màn tự khai 1 mảng `[0,8,10]`/`[0,5,8,10]` rồi
 * render `<select>` riêng, không có cách chọn "Không chịu thuế" hay nhập % tuỳ ý.
 * Gộp lại 1 nơi cho cả 2 lựa chọn mới:
 *   · KCT (Không chịu thuế) → ghi `vatRate = 0`, y hệt chọn "0%" — AN TOÀN, không đụng công thức
 *     tính thuế đang chạy ở nơi khác (user chốt: "giống hệt toán của 0%, chỉ khác nhãn").
 *   · Khác... → mở ô nhập % tự do.
 * ⚠ Vì KCT lưu lại y hệt số 0 nên KHÔNG phân biệt được với "0%" sau khi tải lại trang — `mode` chỉ
 * là UI cục bộ, giữ đúng nhãn trong lúc đang sửa. Mở lại bản ghi cũ có KCT sẽ hiện lại "0%" (chấp
 * nhận được — không đụng dữ liệu/công thức, chỉ tiện lối chọn lúc nhập liệu).
 */
export const VatRateSelect = ({
  value, rates, onChange, className = '', selectClassName = '', defaultKct = false,
}: {
  value: number
  rates: readonly number[]
  onChange: (n: number) => void
  className?: string
  selectClassName?: string
  /** true = form MỚI mặc định hiện nhãn "KCT" thay vì "0%" khi value=0 (chỉ ảnh hưởng NHÃN lúc mount,
   *  không đổi hành vi các nơi gọi khác chưa truyền prop này — user 24/08). */
  defaultKct?: boolean
}): JSX.Element => {
  const [mode, setMode] = useState<'rate' | 'kct' | 'custom'>(
    () => (defaultKct && value === 0) ? 'kct' : (rates.includes(value) ? 'rate' : 'custom'),
  )
  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      {/* select THẬT (không qua component `Select`) — `Select` khoá cứng className=fieldCls, bỏ qua
          className truyền vào, nên nơi nào cần cỡ gọn trong bảng (AR/AP) sẽ không áp được. */}
      <select
        value={mode === 'kct' ? 'KCT' : mode === 'custom' ? 'KHAC' : String(value)}
        aria-label="Thuế suất"
        onChange={(e) => {
          const v = e.target.value
          if (v === 'KCT') { setMode('kct'); onChange(0) }
          else if (v === 'KHAC') { setMode('custom') }
          else { setMode('rate'); onChange(Number(v)) }
        }}
        className={selectClassName === '' ? fieldCls : selectClassName}
      >
        {rates.map((r) => <option key={r} value={r}>{r}%</option>)}
        <option value="KCT">KCT</option>
        <option value="KHAC">KHAC</option>
      </select>
      {mode === 'custom' && (
        <div className="flex items-center gap-1 shrink-0">
          <NumberInput decimal value={value} onChange={(n) => { onChange(Math.min(100, Math.max(0, n))) }} placeholder="0" className="!w-16 !text-right" />
          <span className="text-xs text-slate-500">%</span>
        </div>
      )}
    </div>
  )
}

/**
 * `ui.tsx:657`. Rê chuột mở dropdown TỰ VẼ (MultiSelect · SearchSelect) — cùng 3 chốt như
 * `HoverDetails`: chỉ gắn khi máy CÓ chuột · đang mở do rê mà bấm thì GHIM (không thì mất thao tác
 * bấm) · quyết định NGOÀI setState updater (StrictMode gọi updater 2 lần sẽ lật ngược cờ ghim).
 */
export const useHoverOpen = (open: boolean, setOpen: (v: boolean) => void): {
  wrap: { onMouseEnter?: () => void; onMouseLeave?: () => void }
  /**
   * Gắn lên LỚP PHỦ `fixed inset-0` (bắt click ra ngoài) của menu header — bản cũ `useHoverMenu` (`Layout.tsx:23-58`):
   * lớp phủ nằm TRONG thẻ bọc nên `onMouseLeave` không bao giờ bắn; rê trúng lớp phủ = chuột đã ra ngoài menu.
   */
  backdrop: { onMouseEnter?: () => void }
  onBtn: () => void
  unpin: () => void
} => {
  // ⚠ Bản cũ viết `openRef.current = open` THẲNG TRONG THÂN HÀM (`ui.tsx:734`). KHÔNG bê sang:
  //   luật lint `react-hooks/refs` chặn ghi ref lúc vẽ, và nó chặn có lý — React có thể vẽ lại một
  //   lượt rồi bỏ, ref khi đó giữ giá trị của lượt bị bỏ. Đồng bộ trong effect là khuôn chính thức;
  //   hành vi không đổi vì `onBtn` chỉ đọc ref lúc NGƯỜI DÙNG BẤM, tức luôn sau khi effect đã chạy.
  const openRef = useRef(open)
  const pinned = useRef(false)
  const closeT = useRef<number | undefined>(undefined)
  const hoverOk = canHoverDevice()
  useEffect(() => { openRef.current = open }, [open])
  useEffect(() => () => { window.clearTimeout(closeT.current) }, [])
  // đóng rồi thì bỏ ghim
  useEffect(() => { if (!open) pinned.current = false }, [open])
  const leave = (): void => { if (!pinned.current) closeT.current = window.setTimeout(() => { setOpen(false) }, 220) }
  return {
    wrap: hoverOk ? {
      onMouseEnter: () => { window.clearTimeout(closeT.current); setOpen(true) },
      onMouseLeave: leave,
    } : {},
    backdrop: hoverOk ? { onMouseEnter: leave } : {},
    onBtn: () => {
      if (hoverOk && openRef.current && !pinned.current) { pinned.current = true; return }
      pinned.current = !openRef.current
      setOpen(!openRef.current)
    },
    unpin: () => { pinned.current = false },
  }
}

export interface Option {
  value: string
  label: string
  /**
   * Nhãn NGẮN cho ô đã đóng, khi nhãn đầy đủ quá dài để đứng đó (bản cũ `VendorPicker codeOnly`,
   * `Az.tsx:4307`). Ví dụ ô Đối tác dự kiến: đóng lại chỉ hiện `NCC_HOANGLONG`, còn danh sách sổ
   * xuống hiện `NCC_HOANGLONG · CÔNG TY TNHH VẬN TẢI HOÀNG LONG · MST 0309887766`.
   *
   * ⚠ Đây KHÔNG phải để giấu bớt thông tin: người đang CHỌN cần tên và MST để khỏi bấm nhầm hai
   *   nhà thầu trùng mã đầu, người đã chọn xong thì chỉ cần biết mình chọn gì. Bỏ trống ⇒ dùng
   *   `label` cho cả hai chỗ, tức mọi ô đang có không đổi gì.
   */
  short?: string
  /**
   * Chữ THÊM để dò mà không hiện — bản cũ `CustomerPicker` dò cả `name` lẫn `invoiceName` (`Az.tsx:4262`) trong khi nhãn chỉ
   * hiện một trong hai. Chỉ `PickSelect` đọc ô này.
   */
  keywords?: string
}

/** `ui.tsx:671`. Dropdown lọc nhiều lựa chọn (có ô tick). value = mảng id đã chọn. */
export const MultiSelect = ({
  options, value, onChange, allLabel,
  selectAllLabel = 'Chọn tất cả', clearAllLabel = 'Bỏ chọn tất cả',
  ariaLabel, disabled = false,
}: {
  options: Option[]
  value: string[]
  onChange: (next: string[]) => void
  allLabel: string
  selectAllLabel?: string
  clearAllLabel?: string
  /** Nhãn cho nút mở — CẦN khi một màn có NHIỀU ô cùng `allLabel` (bảng cấp duyệt: mỗi bước một ô,
   *  mười nút cùng đọc "＋ Chọn người…" thì trình đọc màn hình lẫn bộ kiểm đều không phân biệt nổi). */
  ariaLabel?: string
  disabled?: boolean
}): JSX.Element => {
  const [open, setOpen] = useState(false)
  // GÕ TÌM trong dropdown (user 26/07) — nhiều KH/NCC không kéo nổi
  const [q, setQ] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  /* ⚠ XOÁ Ô TÌM LÚC MỞ, TRONG TRÌNH XỬ LÝ SỰ KIỆN — không phải trong effect.
     Bản cũ đặt `setQ('')` ngay đầu effect (`ui.tsx:690`); luật lint `react-hooks/set-state-in-effect`
     chặn, và chặn cả khi đã có `if (!open) return` đứng trước. Đúng ra thì việc "mở dropdown" là
     một SỰ KIỆN, nên dọn ô tìm thuộc về `setOpenClear` dưới đây chứ không thuộc về effect.
     Người dùng thấy y hệt: mở ra là ô tìm trống. */
  const setOpenClear = (v: boolean): void => { if (v) setQ(''); setOpen(v) }
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) setOpenClear(false)
    }
    document.addEventListener('mousedown', onDown)
    // Mở là focus ngay ô tìm để gõ liền
    const tm = setTimeout(() => searchRef.current?.focus(), 30)
    return () => { document.removeEventListener('mousedown', onDown); clearTimeout(tm) }
  }, [open])
  const toggle = (v: string): void => { onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]) }
  const allValues = options.map((o) => o.value)
  const allSelected = allValues.length > 0 && allValues.every((v) => value.includes(v))
  // Lọc theo từ khóa gõ tay. Hiện ô tìm khi >3 lựa chọn (user 27/07).
  const showSearch = options.length > 3
  const qn = q.trim().toLowerCase()
  const shown = qn !== '' ? options.filter((o) => o.label.toLowerCase().includes(qn)) : options
  // Chọn ĐÚNG 1 chỉ tiêu → hiện luôn tên chỉ tiêu đó (trước chỉ hiện "(1)" nên không biết đang lọc gì)
  const one = value.length === 1 ? options.find((o) => o.value === value[0])?.label : undefined
  const label = value.length === 0 ? allLabel : (one ?? `${allLabel} (${value.length})`)
  const hov = useHoverOpen(open, setOpenClear)
  const scope = qn !== '' ? shown.map((o) => o.value) : allValues
  const scopeAll = scope.length > 0 && scope.every((v) => value.includes(v))
  return (
    <div ref={ref} className="relative" {...(disabled ? {} : hov.wrap)}>
      <button
        type="button"
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={hov.onBtn}
        className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm bg-white outline-none hover:border-slate-300 focus:border-brand-500 disabled:opacity-50"
      >
        <span title={label} className={`max-w-[190px] truncate ${value.length > 0 ? 'text-slate-700 font-medium' : 'text-slate-500'}`}>{label}</span>
        <span className="text-slate-400 text-xs">▾</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 min-w-[12rem] max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg p-1">
          {options.length === 0 ? (
            <div className="px-2 py-1.5 text-xs text-slate-400">—</div>
          ) : (
            <>
              {/* Ô GÕ TÌM — chỉ hiện khi danh sách dài; sticky trên cùng để cuộn vẫn thấy */}
              {showSearch && (
                <div className="sticky top-0 bg-white pb-1 -mt-1 pt-1 z-10">
                  <input
                    ref={searchRef}
                    value={q}
                    aria-label="Gõ để tìm"
                    onChange={(e) => { setQ(e.target.value) }}
                    placeholder="🔍 Gõ để tìm…"
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-brand-500"
                  />
                </div>
              )}
              {/* Chọn tất cả / Bỏ chọn tất cả — theo KẾT QUẢ TÌM khi đang gõ, else toàn bộ */}
              <button
                type="button"
                onClick={() => { onChange(scopeAll ? value.filter((v) => !scope.includes(v)) : [...new Set([...value, ...scope])]) }}
                className="flex w-full items-center justify-center rounded-lg px-2 py-1.5 text-xs font-semibold text-brand-600 hover:bg-brand-50 mb-1 border-b border-slate-100"
              >
                {qn !== ''
                  ? (scopeAll ? `✕ Bỏ chọn ${scope.length} kết quả` : `✓ Chọn ${scope.length} kết quả`)
                  : (allSelected ? `✕ ${clearAllLabel}` : `✓ ${selectAllLabel}`)}
              </button>
              {shown.length === 0
                ? <div className="px-2 py-2 text-xs text-slate-400 text-center">Không tìm thấy &quot;{q}&quot;</div>
                : shown.map((o) => {
                  const checked = value.includes(o.value)
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => { toggle(o.value) }}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-left hover:bg-slate-50"
                    >
                      <span className={`grid place-items-center w-4 h-4 rounded border shrink-0 ${checked ? 'bg-brand-500 border-brand-500 text-white' : 'border-slate-300'}`}>
                        {checked && <span className="text-[10px] leading-none">✓</span>}
                      </span>
                      <span className="flex-1 text-slate-700">{o.label}</span>
                    </button>
                  )
                })}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * `ui.tsx:783`. Dropdown 1-LỰA-CHỌN CÓ GÕ TÌM (user 26/07) — thay `<select>` gốc cho picker/filter
 * KH·NCC nhiều lựa chọn. value = id đang chọn ('' = chưa chọn / tất cả).
 */
export const SearchSelect = ({
  value, onChange, options, placeholder = '— Chọn —', emptyLabel, className = '',
  disabled = false, alwaysSearch = false, ariaLabel,
}: {
  value: string
  onChange: (v: string) => void
  options: Option[]
  placeholder?: string
  /** dòng đầu để bỏ chọn / "Tất cả" (không truyền = không có dòng bỏ chọn) */
  emptyLabel?: string
  className?: string
  disabled?: boolean
  /** luôn hiện ô GÕ TÌM dù ít lựa chọn (VD picker thầu phụ — user 28/07) */
  alwaysSearch?: boolean
  /** Tên của ô cho trình đọc màn hình / ca kiểm — đặt lên nút mở danh sách. */
  ariaLabel?: string
}): JSX.Element => {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [anchor, setAnchor] = useState<DOMRect | null>(null)
  /* ⚠ Cùng lý do như `MultiSelect` ở trên: dọn ô tìm trong HÀNH VI MỞ, không trong effect. */
  const openList = (): void => {
    setQ('')
    if (ref.current !== null) setAnchor(ref.current.getBoundingClientRect())
    setOpen(true)
  }
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      const tgt = e.target as Node
      if (ref.current?.contains(tgt) === true || popRef.current?.contains(tgt) === true) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    const tm = setTimeout(() => searchRef.current?.focus(), 30)
    // Danh sách bung qua PORTAL fixed → phải bám lại vị trí khi cuộn/đổi cỡ (bảng Hạch toán có overflow riêng)
    const repos = (): void => { if (ref.current !== null) setAnchor(ref.current.getBoundingClientRect()) }
    window.addEventListener('scroll', repos, true); window.addEventListener('resize', repos)
    return () => {
      document.removeEventListener('mousedown', onDown); clearTimeout(tm)
      window.removeEventListener('scroll', repos, true); window.removeEventListener('resize', repos)
    }
  }, [open])
  const sel = options.find((o) => o.value === value)
  const qn = q.trim().toLowerCase()
  const shown = qn !== '' ? options.filter((o) => o.label.toLowerCase().includes(qn)) : options
  // Hiện ô GÕ TÌM khi >3 lựa chọn — CÙNG NGƯỠNG với MultiSelect (user 27/07), user 01/08 nhắc lại:
  // danh sách thầu phụ / NCC / khách hàng sau này rất dài, phải gõ tìm chứ không kéo tay.
  const showSearch = alwaysSearch || options.length > 3
  // CLICK-ONLY (user 15/08): bỏ mở-khi-rê-chuột cho SearchSelect (form dropdown hay bung nhầm khi lướt chuột);
  // GIỮ click. Menu chọn mẫu (HoverDetails) vẫn hover như cũ.
  // DROPDOWN BUNG QUA PORTAL + position:fixed (user 15/08) → KHÔNG bị overflow của bảng Hạch toán / modal cắt
  //   (trước dùng absolute nên "click TK Có không thấy list để chọn"). Cùng cách với ComboInput.
  const listStyle: CSSProperties = anchor !== null
    ? (() => {
      const rows = shown.length + (showSearch ? 1 : 0) + (emptyLabel !== undefined ? 1 : 0)
      const H = Math.min(256, rows * 38 + 8)
      let top = anchor.bottom + 4
      if (top + H > window.innerHeight) top = Math.max(8, anchor.top - H - 4)
      return { position: 'fixed', top, left: anchor.left, minWidth: anchor.width, zIndex: 9999 }
    })()
    : {}
  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button" disabled={disabled} onClick={() => { if (open) setOpen(false); else openList() }} aria-label={ariaLabel}
        className="flex w-full items-center justify-between gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm bg-white outline-none hover:border-slate-300 focus:border-brand-500 disabled:bg-slate-50 disabled:text-slate-400"
      >
        {/* Ô ĐÓNG dùng nhãn ngắn nếu có (xem `Option.short`); tooltip vẫn là nhãn ĐẦY ĐỦ. */}
        <span title={sel?.label} className={`truncate ${sel !== undefined ? 'text-slate-700' : 'text-slate-400'}`}>{sel === undefined ? (emptyLabel ?? placeholder) : (sel.short ?? sel.label)}</span>
        <span className="text-slate-400 text-xs shrink-0">▾</span>
      </button>
      {open && !disabled && createPortal(
        <div ref={popRef} style={listStyle} className="w-max max-w-[22rem] max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg p-1">
          {showSearch && (
            <div className="sticky top-0 bg-white pb-1 -mt-1 pt-1 z-10">
              <input
                ref={searchRef} value={q} aria-label="Gõ để tìm"
                onChange={(e) => { setQ(e.target.value) }} placeholder="🔍 Gõ để tìm…"
                onKeyDown={(e) => {
                  // Enter = chọn kết quả ĐẦU TIÊN đang lọc (khỏi phải click); Esc = đóng (user 06/08)
                  const first = shown[0]
                  if (e.key === 'Enter') { e.preventDefault(); if (first !== undefined) { onChange(first.value); setOpen(false) } }
                  else if (e.key === 'Escape') { e.preventDefault(); setOpen(false) }
                }}
                className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-brand-500"
              />
            </div>
          )}
          {emptyLabel !== undefined && (
            <button
              type="button" onClick={() => { onChange(''); setOpen(false) }}
              className={`flex w-full items-center rounded-lg px-2 py-1.5 text-sm text-left hover:bg-slate-50 ${value === '' ? 'font-semibold text-brand-600' : 'text-slate-500'}`}
            >{emptyLabel}</button>
          )}
          {shown.length === 0
            ? <div className="px-2 py-2 text-xs text-slate-400 text-center">Không tìm thấy &quot;{q}&quot;</div>
            : shown.map((o) => (
              <button
                key={o.value} type="button" onClick={() => { onChange(o.value); setOpen(false) }}
                className={`flex w-full items-center rounded-lg px-2 py-1.5 text-sm text-left hover:bg-slate-50 ${o.value === value ? 'font-semibold text-brand-600 bg-brand-50' : 'text-slate-700'}`}
              >{o.label}</button>
            ))}
        </div>,
        document.body,
      )}
    </div>
  )
}

/** `ui.tsx:867`. Ô gõ tự do + dropdown gợi ý (combobox): gõ để lọc hoặc bấm ▾ để chọn lại. */
export const ComboInput = ({
  value, onChange, options, placeholder, className = '',
}: {
  value: string
  onChange: (v: string) => void
  options: string[]
  placeholder?: string
  className?: string
}): JSX.Element => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [anchor, setAnchor] = useState<DOMRect | null>(null)
  const openList = (): void => {
    if (ref.current !== null) setAnchor(ref.current.getBoundingClientRect())
    setOpen(true)
  }
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      const tgt = e.target as Node
      if (ref.current?.contains(tgt) === true || popRef.current?.contains(tgt) === true) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => { document.removeEventListener('mousedown', onDown) }
  }, [open])
  const q = value.trim().toLowerCase()
  const exact = options.some((o) => o.toLowerCase() === q)
  const matched = q !== '' && !exact ? options.filter((o) => o.toLowerCase().includes(q)) : options
  // Nếu không có kết quả khớp thì vẫn hiện tất cả để người dùng có thể chọn
  const filtered = matched.length > 0 ? matched : options
  // Danh sách bung qua PORTAL + position:fixed → KHÔNG bị modal (overflow) cắt (bug ▾ không mở được)
  const listStyle: CSSProperties = anchor !== null
    ? (() => {
      const H = Math.min(224, filtered.length * 38 + 8)
      let top = anchor.bottom + 4
      if (top + H > window.innerHeight) top = Math.max(8, anchor.top - H - 4)
      return { position: 'fixed', top, left: anchor.left, width: anchor.width, zIndex: 9999 }
    })()
    : {}
  return (
    <div ref={ref} className="relative w-full">
      <input
        value={value}
        placeholder={placeholder}
        style={{ paddingRight: '2rem' }}
        onChange={(e) => {
          onChange(e.target.value)
          if (ref.current !== null) setAnchor(ref.current.getBoundingClientRect())
          if (!open) setOpen(true)
        }}
        onFocus={openList}
        className={`${fieldCls} ${className}`}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label="Mở danh sách"
        onClick={() => { if (open) setOpen(false); else openList() }}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-brand-600 text-xs leading-none"
      >
        ▾
      </button>
      {open && filtered.length > 0 && createPortal(
        <div ref={popRef} style={listStyle} className="max-h-56 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg p-1">
          {filtered.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => { onChange(o); setOpen(false) }}
              className={`block w-full text-left rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50 ${o === value ? 'text-brand-600 font-medium' : 'text-slate-700'}`}
            >
              {o}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  )
}

/**
 * ══ Ô GÕ-ĐỂ-CHỌN KHÁCH HÀNG / NHÀ CUNG CẤP — chép `CustomerPicker` · `VendorPicker`
 *    (`Az.tsx:4246` · `Az.tsx:4305`, user 27/07) ═══════════════════════════════════════════════
 *
 * ⚠ ĐÂY KHÔNG PHẢI `SearchSelect`, và đừng gộp hai thứ lại. Bản cũ cố ý có HAI kiểu ô chọn:
 *   · `SearchSelect` — NÚT bấm ra danh sách, ô tìm nằm TRONG danh sách. Dùng cho tập chọn ĐÓNG và
 *     ngắn: mốc ngày, phiên bảng giá, tài khoản kế toán.
 *   · Ô này — bản thân Ô LÀ chỗ gõ. Dùng cho KH/NCC: danh sách hàng trăm dòng, người dùng đã biết
 *     sẵn mã cần gõ, và bắt họ bấm một nút rồi mới tới được chỗ gõ là thêm một cú bấm cho việc họ
 *     làm vài chục lần mỗi ngày.
 *
 * ⭐ GÕ RỖNG Ô = BỎ CHỌN (bản cũ `Az.tsx:4270`): xoá trắng ô là cách người ta huỷ lựa chọn, không
 *   phải cách lọc ra danh sách rỗng.
 * ⭐ CHỌN BẰNG `onMouseDown` + `preventDefault`, KHÔNG phải `onClick`: `click` nổ SAU `blur`, mà
 *   `blur` đóng danh sách ⇒ nút biến mất trước khi cú bấm tới nơi.
 *
 * ⚠ Danh sách bung qua PORTAL + `position:fixed` như `SearchSelect` — bản cũ dùng `absolute` và
 *   chỗ nào cũng chạy vì thân hộp thoại bản cũ không cuộn riêng. Ở đây ô này nằm trong `Modal` có
 *   vùng cuộn, `absolute` sẽ bị cắt mất hoặc trôi theo khi cuộn.
 */
export const PickSelect = ({
  value, onChange, options, placeholder = '— Chọn —', emptyLabel, className = '',
  disabled = false, ariaLabel, noMatchLabel = 'Không tìm thấy', listTitle = 'Xem danh sách',
  extraRow,
}: {
  value: string
  onChange: (v: string) => void
  /** `label` hiện trong danh sách (mã · tên · MST); `short` là thứ đọng lại trong ô sau khi chọn. */
  options: Option[]
  placeholder?: string
  /** Dòng đầu để BỎ CHỌN, VD "— Chưa biết ai chạy —". Bỏ trống ⇒ không có dòng đó. */
  emptyLabel?: string
  className?: string
  disabled?: boolean
  ariaLabel?: string
  noMatchLabel?: string
  listTitle?: string
  /** Dòng cuối danh sách, VD "＋ Tạo khách hàng mới" — nhận đúng chữ người dùng vừa gõ. */
  extraRow?: { label: (typed: string) => string; onPick: (typed: string) => void }
}): JSX.Element => {
  const [open, setOpen] = useState(false)
  /** `null` = CHƯA GÕ GÌ ⇒ ô hiện giá trị đang chọn và danh sách không bị lọc. */
  const [q, setQ] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [anchor, setAnchor] = useState<DOMRect | null>(null)

  const openList = (): void => {
    if (ref.current !== null) setAnchor(ref.current.getBoundingClientRect())
    setOpen(true)
  }
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      const tgt = e.target as Node
      if (ref.current?.contains(tgt) === true || popRef.current?.contains(tgt) === true) return
      setOpen(false); setQ(null)
    }
    document.addEventListener('mousedown', onDown)
    const repos = (): void => { if (ref.current !== null) setAnchor(ref.current.getBoundingClientRect()) }
    window.addEventListener('scroll', repos, true); window.addEventListener('resize', repos)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('scroll', repos, true); window.removeEventListener('resize', repos)
    }
  }, [open])

  const sel = options.find((o) => o.value === value)
  const typed = (q ?? '').trim()
  const kw = typed.toLowerCase()
  /* Trần 60 dòng như bản cũ (`Az.tsx:4263`): danh sách KH/NCC dài hàng nghìn, vẽ hết là khựng —
     ai cần dòng thứ 61 thì gõ thêm một chữ. */
  const shown = (kw === '' ? options : options.filter((o) => `${o.label} ${o.keywords ?? ''}`.toLowerCase().includes(kw))).slice(0, 60)

  const pick = (v: string): void => { onChange(v); setOpen(false); setQ(null) }

  const listStyle: CSSProperties = anchor !== null
    ? (() => {
      const rows = shown.length + (emptyLabel === undefined ? 0 : 1) + (extraRow === undefined ? 0 : 1)
      const H = Math.min(256, rows * 34 + 8)
      let top = anchor.bottom + 4
      if (top + H > window.innerHeight) top = Math.max(8, anchor.top - H - 4)
      return { position: 'fixed', top, left: anchor.left, minWidth: anchor.width, zIndex: 9999 }
    })()
    : {}

  return (
    <div ref={ref} className={`relative ${className}`}>
      <div className="flex">
        <input
          value={q ?? (sel === undefined ? '' : (sel.short ?? sel.label))}
          placeholder={placeholder} disabled={disabled}
          {...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel })}
          onFocus={() => { setQ(null); openList() }}
          onChange={(e) => {
            setQ(e.target.value)
            openList()
            // Xoá trắng ô = bỏ chọn — xem chú thích đầu component.
            if (e.target.value.trim() === '') onChange('')
          }}
          className="w-full rounded-lg rounded-r-none border border-slate-200 px-2.5 py-1.5 text-sm bg-white outline-none hover:border-slate-300 focus:border-brand-500 disabled:bg-slate-50 disabled:text-slate-400"
        />
        <button
          type="button" tabIndex={-1} title={listTitle} aria-label={listTitle} disabled={disabled}
          onClick={() => { setQ(null); if (open) setOpen(false); else openList() }}
          className="shrink-0 w-7 rounded-r-lg border border-l-0 border-slate-200 bg-slate-50 text-[10px] text-slate-500 hover:bg-slate-100 disabled:bg-slate-50"
        >▾</button>
      </div>
      {open && !disabled && createPortal(
        <div ref={popRef} style={listStyle} className="w-max max-w-[22rem] max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg p-1 text-[12px]">
          {emptyLabel !== undefined && (
            <button
              type="button" onMouseDown={(e) => { e.preventDefault(); pick('') }}
              className={`block w-full rounded-lg px-2.5 py-1.5 text-left italic hover:bg-brand-50 ${value === '' ? 'font-semibold text-brand-600' : 'text-slate-500'}`}
            >{emptyLabel}</button>
          )}
          {shown.length === 0
            ? <div className="px-2.5 py-2 italic text-slate-400">{noMatchLabel}</div>
            : shown.map((o) => (
              <button
                key={o.value} type="button" onMouseDown={(e) => { e.preventDefault(); pick(o.value) }}
                className={`block w-full rounded-lg px-2.5 py-1.5 text-left hover:bg-brand-50 ${o.value === value ? 'bg-brand-50 font-bold text-brand-700' : 'text-slate-700'}`}
              >{o.label}</button>
            ))}
          {extraRow !== undefined && (
            <button
              type="button" onMouseDown={(e) => { e.preventDefault(); setOpen(false); extraRow.onPick(typed) }}
              className="sticky bottom-0 block w-full rounded-lg border-t border-slate-100 bg-white px-2.5 py-2 text-left font-bold text-brand-600 hover:bg-brand-50"
            >{extraRow.label(typed)}</button>
          )}
        </div>,
        document.body,
      )}
    </div>
  )
}
